import { useCallback, useEffect, useRef, useState } from "react";

const BAR_COUNT = 28;
const IDLE_LEVELS = Array.from({ length: BAR_COUNT }, () => 0.15);

export const useAudioAnalyser = () => {
  const [levels, setLevels] = useState<number[]>(() => [...IDLE_LEVELS]);

  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const elementRef = useRef<HTMLAudioElement | null>(null);
  const sourceRef = useRef<
    MediaStreamAudioSourceNode | MediaElementAudioSourceNode | null
  >(null);

  const pauseLoop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setLevels([...IDLE_LEVELS]);
  }, []);

  const stop = useCallback(() => {
    pauseLoop();
    sourceRef.current?.disconnect();
    sourceRef.current = null;
    analyserRef.current?.disconnect();
    analyserRef.current = null;
    elementRef.current = null;
    void audioCtxRef.current?.close();
    audioCtxRef.current = null;
  }, [pauseLoop]);

  const runAnalyserLoop = useCallback((analyser: AnalyserNode) => {
    const data = new Uint8Array(analyser.frequencyBinCount);

    const tick = () => {
      analyser.getByteFrequencyData(data);
      const next = Array.from({ length: BAR_COUNT }, (_, i) => {
        const idx = Math.floor((i / BAR_COUNT) * data.length);
        return Math.max(0.12, data[idx] / 255);
      });
      setLevels(next);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const start = useCallback(
    (stream: MediaStream) => {
      stop();
      const audioCtx = new AudioContext();
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 64;
      const source = audioCtx.createMediaStreamSource(stream);
      source.connect(analyser);

      audioCtxRef.current = audioCtx;
      analyserRef.current = analyser;
      sourceRef.current = source;
      runAnalyserLoop(analyser);
    },
    [stop, runAnalyserLoop],
  );

  const startFromElement = useCallback(
    (audio: HTMLAudioElement) => {
      if (
        elementRef.current === audio &&
        analyserRef.current &&
        audioCtxRef.current
      ) {
        void audioCtxRef.current.resume();
        runAnalyserLoop(analyserRef.current);
        return;
      }

      stop();
      const audioCtx = new AudioContext();
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 64;
      const source = audioCtx.createMediaElementSource(audio);
      source.connect(analyser);
      analyser.connect(audioCtx.destination);

      audioCtxRef.current = audioCtx;
      analyserRef.current = analyser;
      sourceRef.current = source;
      elementRef.current = audio;
      runAnalyserLoop(analyser);
    },
    [stop, runAnalyserLoop],
  );

  useEffect(() => () => stop(), [stop]);

  return {
    levels,
    start,
    startFromElement,
    pauseLoop,
    stop,
    barCount: BAR_COUNT,
  };
};
